import { useState, type ReactElement } from 'react';
import type { EntityTypeDto } from '@gr/core/content/entities/dto';
import { useServices, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Card } from '../../../ui-kit/Card/Card';
import { useConfirm } from '../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { Dialog } from '../../../ui-kit/Dialog/Dialog';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { useBlog } from '../../accounts/workspaces';
import { EntityTypeForm } from './EntityTypeForm/EntityTypeForm';
import { EntityTypeList } from './EntityTypeList/EntityTypeList';

// ENT-UC-1, ENT-UC-2, ENT-UC-3
export function EntityTypesSection({ onChanged }: { onChanged(): void }): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { blog } = useBlog();
  const confirm = useConfirm();
  const types = useResource(() => api.entities.types(blog.id), [api, blog.id]);
  const [editing, setEditing] = useState<EntityTypeDto | 'new' | null>(null);
  const remove = useAction((typeId: string) => api.entities.deleteType(blog.id, typeId));

  const refresh = async (): Promise<void> => {
    await types.reload();
    onChanged();
  };

  const editingType = editing === 'new' ? null : editing;
  return (
    <Card
      title={t('entities.types.title')}
      actions={
        <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
          {t('entities.types.create')}
        </Button>
      }
    >
      <div className="u-stack">
        <p className="u-muted u-small">{t('entities.types.subtitle')}</p>
        {remove.error ? <Alert tone="danger">{te(remove.error)}</Alert> : null}
        {types.loading && !types.data ? (
          <Loading label={t('common.loading')} />
        ) : (
          <EntityTypeList
            blogId={blog.id}
            types={types.data ?? []}
            pending={remove.pending}
            onEdit={setEditing}
            onDelete={async (type) => {
              if (!(await confirm({ title: t('entities.types.deleteConfirm', { name: type.name }), confirmLabel: t('common.delete'), danger: true }))) return;
              if ((await remove.run(type.id)).success) await refresh();
            }}
          />
        )}
      </div>
      {editing ? (
        <Dialog
          open
          wide
          title={editingType ? t('entities.types.edit') : t('entities.types.create')}
          closeLabel={t('common.close')}
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
              <Button type="submit" form="entity-type-form" variant="primary">
                {t('common.save')}
              </Button>
            </>
          }
        >
          <EntityTypeForm
            id="entity-type-form"
            type={editingType}
            otherTypes={(types.data ?? []).filter((other) => other.id !== editingType?.id)}
            onSubmit={async (input) => {
              const result = editingType ? await api.entities.updateType(blog.id, editingType.id, input) : await api.entities.createType(blog.id, input);
              if (result.success) {
                setEditing(null);
                await refresh();
              }
              return result;
            }}
          />
        </Dialog>
      ) : null}
    </Card>
  );
}
